import React, { useMemo } from 'react';
import DataTable from '../../../components/common/DataTable';

const REMINDER_RECIPIENTS_COLUMN_CONFIG = [
  { id: 'name', label: 'Name', defaultVisible: true },
  { id: 'type', label: 'Type', defaultVisible: true },
  { id: 'email', label: 'Email', defaultVisible: true },
  { id: 'mobilePhone', label: 'Phone', defaultVisible: true },
  { id: 'lotNumber', label: 'Lot No.', defaultVisible: true },
  { id: 'recipient_type', label: 'Notice', defaultVisible: true },
  { id: 'status', label: 'Status', defaultVisible: true },
  { id: 'actions', label: 'Actions', defaultVisible: true }
];

const ReminderTable = ({
  data = [],
  title = 'Reminder Recipients',
  loading = false,
  isDark = false,
  onCancelReminder,
  showCancelAllButton = false,
  onCancelAll,
  canCancel = true,
  emptyMessage = 'No reminder recipients found'
}) => {
  const hasQueuedReminders = useMemo(() => canCancel && data.some(r => r.status === 'queued'), [data, canCancel]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        cell: ({ getValue }) => getValue() || '-'
      },
      {
        id: 'type',
        accessorKey: 'type',
        header: 'Type',
        cell: ({ getValue }) => {
          const value = getValue();
          return value === 'owner-agent' ? 'owner agent' : value || '-';
        }
      },
      {
        id: 'email',
        accessorKey: 'email',
        header: 'Email',
        cell: ({ getValue }) => getValue() || '-'
      },
      {
        id: 'mobilePhone',
        accessorKey: 'mobilePhone',
        header: 'Phone',
        cell: ({ getValue }) => getValue() || '-'
      },
      {
        id: 'lotNumber',
        accessorKey: 'lotNumber',
        header: 'Lot No.',
        cell: ({ getValue }) => getValue() || '-'
      },
      {
        id: 'recipient_type',
        accessorKey: 'recipient_type',
        header: 'Notice',
        cell: ({ getValue }) => {
          const value = getValue();
          if (value === 'email') return 'Email';
          if (value === 'sms') return 'Sms';
          return value || '-';
        }
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => {
          const value = getValue();
          const statusConfig = {
            queued: { bg: 'bg-[#fff9db]', text: 'text-[#9a7b00]', label: 'Queued' },
            sent: { bg: 'bg-[#d9fbe6]', text: 'text-[#0a6e29]', label: 'Sent' },
            failed: { bg: 'bg-[#ffe3e3]', text: 'text-[#b42318]', label: 'Failed' },
            cancelled: { bg: 'bg-[#ffe3e3]', text: 'text-[#b42318]', label: 'Cancelled' }
          };

          const config = statusConfig[value] || statusConfig.queued;

          return (
            <span className={`${config.bg} ${config.text} px-[16px] py-[6px] rounded-[78px] text-[13px] font-medium inline-flex items-center justify-center whitespace-nowrap capitalize w-[120px]`}>
              {config.label}
            </span>
          );
        }
      }
    ];

    if (hasQueuedReminders) {
      baseColumns.push({
        id: 'actions',
        accessorKey: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const status = row.original.status;
          if (status !== 'queued') {
            return <span className={`${isDark ? 'text-[#b0b0b0]' : 'text-[#686868]'}`}>-</span>;
          }
          return (
            <button
              onClick={() => onCancelReminder && onCancelReminder(row.original.id)}
              className={`rounded-[10px] px-[8px] sm:px-[10px] py-[6px] flex items-center gap-[6px] border-0 cursor-pointer transition-colors whitespace-nowrap ${
                isDark
                  ? 'bg-red-900 text-red-200 hover:bg-red-800'
                  : 'bg-red-600 text-white hover:bg-red-700'
                }`}
            >
              <span className="font-medium text-[14px] sm:text-[16px] transition-colors duration-200">Cancel</span>
            </button>
          );
        }
      });
    }

    return baseColumns;
  }, [isDark, onCancelReminder, hasQueuedReminders]);




  const queuedCount = data.filter(r => r.status === 'queued').length;

  return (
    <div className={`border-solid border-[1px] rounded-[16px] sm:rounded-[16px] overflow-hidden transition-colors duration-200 ${
      isDark ? 'bg-[#161515] border-[#292929]' : 'bg-white border-[#d9d9d9]'
      }`}>
      <div className={`border-b border-solid border-[1px] h-[56px] sm:h-[68px] flex items-center justify-between transition-colors duration-200 ${
        isDark ? 'bg-[#161515] border-[#292929]' : 'bg-white border-[#d9d9d9]'
        }`}>
        <div className="px-[16px] sm:px-[24px]">
          <h2 className={`m-0 font-bold text-[14px] sm:text-[16px] transition-colors duration-200 ${
            isDark ? 'text-white' : 'text-[#072837]'
            }`}>{title}</h2>
        </div>
        {showCancelAllButton && (
          <div className="px-[16px] sm:px-[24px]">
            <button
              onClick={onCancelAll}
              className={`rounded-[10px] px-[10px] sm:px-[16px] py-[8px] flex items-center gap-[6px] border-0 cursor-pointer transition-colors whitespace-nowrap ${
                isDark
                  ? 'bg-red-900 text-red-200 hover:bg-red-800'
                  : 'bg-red-600 text-white hover:bg-red-700'
                }`}
            >
              <span className="font-semibold text-[14px] sm:text-[16px] transition-colors duration-200">
                {queuedCount > 0 ? `Cancel All Queued (${queuedCount})` : 'Cancel All Reminders'}
              </span>
            </button>
          </div>
        )}
      </div>
      <div className={`overflow-x-auto ${isDark ? '[&::-webkit-scrollbar-track]:bg-[#000000] [&::-webkit-scrollbar-thumb]:bg-[#292929] [&::-webkit-scrollbar-thumb]:hover:bg-[#4d4d4d] [&::-webkit-scrollbar-button]:bg-[#161515]' : '[&::-webkit-scrollbar-track]:bg-[#f5f5f5] [&::-webkit-scrollbar-thumb]:bg-[#ebebeb] [&::-webkit-scrollbar-thumb]:hover:bg-[#e5e5e5] [&::-webkit-scrollbar-button]:bg-[#d9d9d9]'} [&::-webkit-scrollbar]:h-[10px] [&::-webkit-scrollbar]:w-[16px] [&::-webkit-scrollbar-button]:w-[16px] [&::-webkit-scrollbar-button]:h-[18px]`}>
        <DataTable
          data={data}
          columns={columns}
          columnConfig={REMINDER_RECIPIENTS_COLUMN_CONFIG}
          storageKey="reminder_recipients_visible_columns"
          title={title}
          loading={loading}
          isDark={isDark}
          emptyStateText={emptyMessage}
          showTitleRow={false}
        />
      </div>
    </div>
  );
};

export default ReminderTable;
